import { useState, useEffect } from 'react';

interface ScoringRule {
  id: string;
  name: string;
  category: string;
  points: number;
  description: string;
  active: boolean;
}

const STORAGE_KEY = 'cns_scoring_rules';

const DEFAULT_RULES: ScoringRule[] = [
  {
    id: '1',
    name: 'Présence sur liste de surveillance',
    category: 'watchlist',
    points: 4,
    description: "Invité identifié sur une liste de surveillance internationale",
    active: true,
  },
  {
    id: '2',
    name: 'Double enregistrement',
    category: 'double_registration',
    points: 3,
    description: 'Invité enregistré dans plusieurs hôtels simultanément',
    active: true,
  },
  {
    id: '3',
    name: 'Séjour anormal',
    category: 'abnormal_stay',
    points: 2,
    description: 'Durée de séjour inhabituelle ou pattern suspect',
    active: true,
  },
  {
    id: '4',
    name: 'Document invalide',
    category: 'document_issue',
    points: 3,
    description: "Problème détecté avec les documents d'identité",
    active: true,
  },
  {
    id: '5',
    name: 'Visa expiré',
    category: 'visa_expiry',
    points: 2,
    description: "Visa expiré ou proche de l'expiration",
    active: true,
  },
];

const CATEGORIES = [
  { value: 'watchlist', label: 'Liste de surveillance' },
  { value: 'double_registration', label: 'Double enregistrement' },
  { value: 'abnormal_stay', label: 'Séjour anormal' },
  { value: 'document_issue', label: 'Document invalide' },
  { value: 'visa_expiry', label: 'Visa expiré' },
  { value: 'identity_fraud', label: 'Fraude identitaire' },
  { value: 'financial', label: 'Financier' },
  { value: 'behavioral', label: 'Comportemental' },
];

function ScoringRulesPage() {
  const [rules, setRules] = useState<ScoringRule[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ScoringRule | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formPoints, setFormPoints] = useState(1);
  const [formDescription, setFormDescription] = useState('');
  const [formActive, setFormActive] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try { setRules(JSON.parse(stored)); } catch { setRules(DEFAULT_RULES); }
    } else {
      setRules(DEFAULT_RULES);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_RULES));
    }
  }, []);

  const saveRules = (updated: ScoringRule[]) => {
    setRules(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const openAddModal = () => {
    setEditingRule(null);
    setFormName(''); setFormCategory(''); setFormPoints(1);
    setFormDescription(''); setFormActive(true);
    setShowModal(true);
  };

  const openEditModal = (rule: ScoringRule) => {
    setEditingRule(rule);
    setFormName(rule.name); setFormCategory(rule.category);
    setFormPoints(rule.points); setFormDescription(rule.description);
    setFormActive(rule.active);
    setShowModal(true);
  };

  const handleSave = () => {
    if (!formName.trim() || !formCategory || !formDescription.trim()) return;
    if (editingRule) {
      const updated = rules.map(r => r.id === editingRule.id ? {
        ...r, name: formName.trim(), category: formCategory,
        points: formPoints, description: formDescription.trim(), active: formActive,
      } : r);
      saveRules(updated);
    } else {
      const newRule: ScoringRule = {
        id: Date.now().toString(),
        name: formName.trim(), category: formCategory,
        points: formPoints, description: formDescription.trim(), active: formActive,
      };
      saveRules([...rules, newRule]);
    }
    setShowModal(false);
  };

  const handleDelete = (id: string) => {
    saveRules(rules.filter(r => r.id !== id));
    setConfirmDelete(null);
  };

  const toggleActive = (id: string) => {
    saveRules(rules.map(r => r.id === id ? { ...r, active: !r.active } : r));
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      watchlist: 'bg-red-100 text-red-800',
      double_registration: 'bg-orange-100 text-orange-800',
      abnormal_stay: 'bg-yellow-100 text-yellow-800',
      document_issue: 'bg-purple-100 text-purple-800',
      visa_expiry: 'bg-blue-100 text-blue-800',
      identity_fraud: 'bg-pink-100 text-pink-800',
      financial: 'bg-green-100 text-green-800',
      behavioral: 'bg-indigo-100 text-indigo-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryLabel = (value: string) =>
    CATEGORIES.find(c => c.value === value)?.label || value.replace('_', ' ');

  const getPointsColor = (points: number) => {
    if (points >= 4) return 'text-red-600';
    if (points >= 3) return 'text-orange-600';
    if (points >= 2) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">⚖️ Règles de Scoring</h1>
            <p className="mt-2 text-gray-600">Configuration des scores de risque pour l'analyse de sécurité</p>
          </div>
          <button
            onClick={openAddModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Ajouter une règle
          </button>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total règles</div>
            <div className="text-2xl font-bold text-gray-900">{rules.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Règles actives</div>
            <div className="text-2xl font-bold text-green-600">{rules.filter(r => r.active).length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Points max cumulés</div>
            <div className="text-2xl font-bold text-red-600">{rules.filter(r => r.active).reduce((s, r) => s + r.points, 0)}</div>
          </div>
        </div>

        {/* Liste des règles */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Règles configurées ({rules.length})</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {rules.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                Aucune règle configurée. Cliquez sur "Ajouter une règle" pour commencer.
              </div>
            ) : (
              rules.map((rule) => (
                <div key={rule.id} className={`px-6 py-4 hover:bg-gray-50 transition ${!rule.active ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{rule.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(rule.category)}`}>
                          {getCategoryLabel(rule.category)}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${rule.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                          {rule.active ? 'ACTIF' : 'INACTIF'}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{rule.description}</p>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditModal(rule)} className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition">
                          Modifier
                        </button>
                        <button onClick={() => toggleActive(rule.id)} className={`px-3 py-1 text-sm rounded transition ${rule.active ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
                          {rule.active ? 'Désactiver' : 'Activer'}
                        </button>
                        {confirmDelete === rule.id ? (
                          <span className="flex items-center gap-2">
                            <span className="text-sm text-red-600">Confirmer ?</span>
                            <button onClick={() => handleDelete(rule.id)} className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700">Oui</button>
                            <button onClick={() => setConfirmDelete(null)} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Non</button>
                          </span>
                        ) : (
                          <button onClick={() => setConfirmDelete(rule.id)} className="px-3 py-1 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100 transition">
                            Retirer
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 text-right">
                      <div className={`text-3xl font-bold ${getPointsColor(rule.points)}`}>{rule.points}</div>
                      <div className="text-xs text-gray-500">points</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Système de scoring</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>Le score total de risque est calculé en additionnant les points de toutes les règles déclenchées :</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>🟢 0 points : Aucun risque</li>
                  <li>🔵 1 point : Risque faible</li>
                  <li>🟡 2 points : Risque moyen</li>
                  <li>🟠 3 points : Risque élevé</li>
                  <li>🔴 4+ points : Risque critique</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Ajouter/Modifier */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingRule ? 'Modifier la règle' : 'Ajouter une règle'}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input type="text" value={formName} onChange={e => setFormName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nom de la règle" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                <select value={formCategory} onChange={e => setFormCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Sélectionner...</option>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Points (1-5) *</label>
                <input type="number" min={1} max={5} value={formPoints} onChange={e => setFormPoints(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea value={formDescription} onChange={e => setFormDescription(e.target.value)} rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description de la règle" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="rule-active" checked={formActive} onChange={e => setFormActive(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <label htmlFor="rule-active" className="text-sm text-gray-700">Règle active</label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                Annuler
              </button>
              <button onClick={handleSave}
                disabled={!formName.trim() || !formCategory || !formDescription.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                {editingRule ? 'Enregistrer' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScoringRulesPage;
